import { Mountain, Sparkles, Car, Sun, Trees, Armchair, Route, Flower2 } from "lucide-react";
import PhotoFrame from "./PhotoFrame";

const HIGHLIGHTS = [
  {
    title: "Foothills setting",
    body: "A peaceful Dehradun home located at the foothills of Mussoorie, ideal for guests who want both city convenience and hill-town charm.",
    icon: Mountain,
  },
  {
    title: "Evening hill lights",
    body: "As the sun sets, Mussoorie lights up in the distance, creating a memorable evening view from the property.",
    icon: Sparkles,
  },
  {
    title: "Easy Mussoorie access",
    body: "Mussoorie is approximately 21 km away and can be reached in about 45 minutes by car, making ChiMano House a convenient base for slow stays and day trips.",
    icon: Car,
  },
];

const PHOTOS = [
  { label: "Daytime view towards Mussoorie", icon: Mountain, span: "col-span-12 md:col-span-7", ratio: "aspect-[16/10]" },
  { label: "Evening lights of Mussoorie", icon: Sparkles, span: "col-span-12 md:col-span-5", ratio: "aspect-[4/3]" },
  { label: "Open compound with hill backdrop", icon: Trees, span: "col-span-12 md:col-span-5", ratio: "aspect-[4/3]" },
  { label: "Outdoor seating facing the hills", icon: Armchair, span: "col-span-12 md:col-span-7", ratio: "aspect-[16/10]" },
  { label: "Driveway or approach to ChiMano House", icon: Route, span: "col-span-12 md:col-span-6", ratio: "aspect-[4/3]" },
  { label: "Garden and orchard with distant hills", icon: Flower2, span: "col-span-12 md:col-span-6", ratio: "aspect-[4/3]" },
];

export default function Mussoorie({ images = [] }) {
  // Match each labelled slot with the uploaded image at the corresponding `order`
  // (1-indexed). Missing slots gracefully fall back to the elegant placeholder.
  const imageBySlot = (slot) => images.find((img) => (img.order || 0) === slot);

  return (
    <section
      id="mussoorie"
      data-testid="section-mussoorie"
      className="relative py-24 md:py-32 bg-[#F7F5F0] overflow-hidden"
    >
      {/* Soft hill silhouette as a backdrop */}
      <svg
        aria-hidden="true"
        viewBox="0 0 1440 420"
        preserveAspectRatio="none"
        className="pointer-events-none absolute top-12 left-0 right-0 w-full h-[340px] opacity-[0.07]"
      >
        <path d="M0 320 L120 240 L240 280 L380 180 L520 230 L680 130 L820 200 L960 110 L1100 190 L1240 150 L1440 220 L1440 420 L0 420 Z" fill="#2C392F" />
        <path d="M0 360 L160 300 L320 330 L460 260 L600 300 L740 240 L880 290 L1020 230 L1160 280 L1300 240 L1440 290 L1440 420 L0 420 Z" fill="#985F47" opacity="0.65" />
      </svg>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-end mb-16 md:mb-20">
          <div className="lg:col-span-7 reveal">
            <div className="inline-flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#985F47]" />
              <span className="text-[11px] uppercase tracking-[0.35em] text-[#985F47]">A foothills stay</span>
            </div>
            <h2 className="serif text-4xl md:text-5xl lg:text-[64px] leading-[1.02] tracking-tightest text-[#2C392F] font-light">
              Mussoorie in the distance.
              <br />
              <span className="italic text-[#985F47]">Dehradun at your doorstep.</span>
            </h2>
          </div>

          <div className="lg:col-span-5 reveal space-y-5 text-[#4A4D4A] text-base md:text-lg leading-relaxed">
            <p>
              ChiMano House sits at the foothills of Mussoorie, offering a stay that feels calm, open and
              quietly scenic. By day, the hill town can be seen in the distance. By evening, the lights of
              Mussoorie begin to glow across the hills, turning the view into a soft constellation of
              twinkling lights.
            </p>
            <p>
              It is close enough for a beautiful drive up to Mussoorie, yet far enough to return to the
              privacy, space and ease of a fully serviced home in Dehradun.
            </p>
            <div className="flex items-center gap-3 pt-2">
              <Sun size={18} className="text-[#985F47]" strokeWidth={1.5} />
              <span className="serif italic text-[#2C392F]">
                The hills change colour from dawn to dusk.
              </span>
            </div>
          </div>
        </div>

        {/* Three highlight cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-16 md:mb-20">
          {HIGHLIGHTS.map((h, i) => {
            const Icon = h.icon;
            return (
              <article
                key={h.title}
                data-testid={`mussoorie-highlight-${i}`}
                className="reveal bg-[#EFEAE4] border border-[#2C392F]/10 rounded-2xl p-8 md:p-10 hover:border-[#985F47]/40 transition-all flex flex-col"
              >
                <span className="h-12 w-12 rounded-full bg-[#F7F5F0] border border-[#2C392F]/10 flex items-center justify-center">
                  <Icon className="text-[#985F47]" size={22} strokeWidth={1.4} />
                </span>
                <h3 className="serif text-[26px] md:text-[28px] text-[#2C392F] leading-tight mt-6">
                  {h.title}
                </h3>
                <p className="text-[#4A4D4A] text-[15px] leading-relaxed mt-3">{h.body}</p>
              </article>
            );
          })}
        </div>

        {/* Six photograph placeholders, asymmetric editorial grid */}
        <div className="grid grid-cols-12 gap-4 md:gap-5">
          {PHOTOS.map((p, i) => {
            const img = imageBySlot(i + 1);
            return (
              <div key={p.label} data-testid={`mussoorie-photo-${i}`} className={`reveal ${p.span}`}>
                <PhotoFrame
                  image={img}
                  placeholderLabel={p.label}
                  placeholderCaption="Mussoorie · view"
                  placeholderIcon={p.icon}
                  ratio={p.ratio}
                  index={i + 1}
                />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
